import { useEffect, useState } from "react"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { adminApiService } from "@/features/admin/services/admin-api-service"
import type { ChannelStatusHistory } from "@/features/admin/types"
import { ArrowRight, Clock, History } from "lucide-react"

interface ChannelStatusHistoryModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  channelId: string
  channelName?: string
}

// Helper to format datetime
function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
}

// Helper to get status badge variant
function getStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "connected":
      return "default"
    case "connecting":
      return "secondary"
    case "disconnected":
      return "outline"
    case "hibernated":
      return "outline"
    case "failed":
      return "destructive"
    default:
      return "outline"
  }
}

// Helper to get status label
function getStatusLabel(status: string): string {
  switch (status) {
    case "connected":
      return "Conectado"
    case "connecting":
      return "Conectando"
    case "disconnected":
      return "Desconectado"
    case "hibernated":
      return "Hibernado"
    case "failed":
      return "Falha"
    default:
      return status || "Desconhecido"
  }
}

/**
 * Modal to display channel status change history
 */
export function ChannelStatusHistoryModal({
  open,
  onOpenChange,
  channelId,
  channelName,
}: ChannelStatusHistoryModalProps) {
  const [history, setHistory] = useState<ChannelStatusHistory[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open && channelId) {
      fetchHistory()
    }
  }, [open, channelId])

  const fetchHistory = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await adminApiService.listResource<ChannelStatusHistory>(
        "channel_status_history",
        {
          channel_id: `eq.${channelId}`,
          order: "changed_at.desc",
          limit: 50,
        }
      )
      setHistory(response.data)
    } catch (err) {
      console.error("Error fetching channel status history:", err)
      setError("Erro ao carregar histórico de status")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Histórico de Status do Canal
          </DialogTitle>
          <DialogDescription>
            {channelName
              ? `Histórico de mudanças de status para o canal "${channelName}"`
              : "Histórico de mudanças de status do canal"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          {loading && (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-4 rounded-lg border p-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-destructive py-8 text-center">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && history.length === 0 && (
            <div className="text-muted-foreground py-8 text-center">
              <History className="mx-auto mb-4 h-12 w-12 opacity-50" />
              <p>Nenhum histórico de status encontrado</p>
              <p className="mt-1 text-sm">O histórico será registrado a partir de agora</p>
            </div>
          )}

          {!loading && !error && history.length > 0 && (
            <div className="space-y-3">
              {history.map((item, index) => (
                <div
                  key={item.id || item._id}
                  className="bg-muted/30 hover:bg-muted/50 flex items-start gap-3 rounded-lg border p-3 transition-colors"
                >
                  {/* Timeline indicator */}
                  <div className="flex flex-col items-center">
                    <div className="bg-primary h-3 w-3 rounded-full" />
                    {index < history.length - 1 && (
                      <div className="bg-border mt-1 h-full min-h-[20px] w-0.5" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    {/* Status change */}
                    <div className="flex flex-wrap items-center gap-2">
                      {item.old_status && (
                        <>
                          <Badge variant={getStatusVariant(item.old_status)}>
                            {getStatusLabel(item.old_status)}
                          </Badge>
                          <ArrowRight className="text-muted-foreground h-4 w-4" />
                        </>
                      )}
                      <Badge variant={getStatusVariant(item.new_status)}>
                        {getStatusLabel(item.new_status)}
                      </Badge>
                    </div>

                    {/* Timestamp and source */}
                    <div className="text-muted-foreground mt-2 flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDateTime(item.changed_at)}</span>
                      </div>
                      {item.source && (
                        <span className="bg-muted rounded px-2 py-0.5 text-xs">{item.source}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
